import React, { useState, useEffect } from "react";

export default function Libros({
  products = [],
  onProductClick,
  rowLimit = 1,
  text = "Productos",
  groupByAspectRatio = true, // Agrupar por proporción de aspecto
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [itemsPerRow, setItemsPerRow] = useState(5); // Número de productos por fila inicial
  const [aspectRatios, setAspectRatios] = useState([]);

  // Actualiza el número de productos por fila dependiendo del ancho de la ventana
  useEffect(() => {
    const updateItemsPerRow = () => {
      const width = window.innerWidth;
      if (width >= 1200) {
        setItemsPerRow(5); // Para pantallas grandes (>=1200px), 5 productos por fila
      } else if (width >= 900) {
        setItemsPerRow(4); // Para pantallas medianas (768px a 1199px), 4 productos por fila
      } else if (width >= 640) {
        setItemsPerRow(3); // Para pantallas medianas (768px a 1199px), 3 productos por fila
      } else if (width >= 480) {
        setItemsPerRow(2); // Para pantallas pequeñas (480px a 767px), 2 productos por fila
      } else {
        setItemsPerRow(1); // Para pantallas muy pequeñas (<480px), 1 producto por fila
      }
    };

    updateItemsPerRow();

    window.addEventListener("resize", updateItemsPerRow);
    return () => window.removeEventListener("resize", updateItemsPerRow);
  }, []);

  // Calcula las proporciones de aspecto de cada imagen
  useEffect(() => {
    const calculateAspectRatios = async () => {
      const ratios = await Promise.all(
        products.map(async (product) => {
          const img = new Image();
          img.src = product.image;
          await img.decode();
          return img.width / img.height;
        }),
      );
      setAspectRatios(ratios);
    };
    calculateAspectRatios();
  }, [products]);

  // Utilidad: obtener categoría desde el nombre
  const getCategory = (name = "") => {
    const n = name.toLowerCase();
    if (n.startsWith("preescolar")) return "Preescolar";
    if (n.startsWith("primaria")) return "Primaria";
    if (n.startsWith("secundaria")) return "Secundaria";
    return "Otros";
  };

  // Organiza los productos por categoría. Se mantiene aspectRatio solo para el render.
  const groupedByCategory = products.reduce((acc, product, index) => {
    const cat = getCategory(product.name);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ ...product, aspectRatio: aspectRatios[index] });
    return acc;
  }, {});

  // Orden sugerido de grupos
  const groupOrder = ["Preescolar", "Primaria", "Secundaria", "Otros"];
  const organizedProducts = groupOrder
    .filter((g) => groupedByCategory[g]?.length)
    .reduce((acc, g) => {
      acc[g] = groupedByCategory[g];
      return acc;
    }, {});

  const toggleExpand = () => setIsExpanded((prev) => !prev);

  return (
    <div className="bg-gray-300 px-4 pt-10 md:px-10 lg:px-20">
      {/* Título */}
      <div className="mb-4 rounded-lg bg-sky-400 py-2 text-center text-xl font-semibold text-sky-900 sm:text-2xl md:text-3xl lg:text-4xl">
        {text}
      </div>

      {/* Contenedor de Tarjetas */}
      <div
        className={`relative w-full rounded-lg bg-gray-300 py-4 transition-all duration-300 ${
          isExpanded ? "h-full overflow-y-scroll" : "h-auto"
        }`}
      >
        {Object.keys(organizedProducts).map((group, idx) => (
          <div
            key={idx}
            className="mb-6 grid gap-6"
            style={{
              gridTemplateColumns: `repeat(${itemsPerRow}, minmax(0, 1fr))`,
            }}
          >
            {organizedProducts[group]
              .slice(0, isExpanded ? undefined : rowLimit * itemsPerRow)
              .map((product, index) => (
                <div
                  key={index}
                  onClick={() => onProductClick(product)}
                  className="group relative transform cursor-pointer rounded-lg bg-white p-2 shadow-sm shadow-black transition-transform duration-200 hover:scale-105"
                >
                  {/* Imagen con proporción variable */}
                  <div
                    className="w-full overflow-hidden rounded-lg"
                    style={{
                      aspectRatio: `${product.aspectRatio || 4 / 6}`,
                    }}
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="mt-2 py-2 text-center">
                    <h3 className="text-sm font-medium">{product.name}</h3>
                    {product.price && (
                      <p className="text-gray-500">${product.price}</p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        ))}

        {/* Botón de Mostrar Más/Menos */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={toggleExpand}
            className="w-full rounded-md bg-sky-400 px-12 py-2 text-lg text-sky-900 transition-all hover:bg-sky-600 sm:py-4 md:text-xl"
          >
            {isExpanded ? "Mostrar menos" : "Mostrar más"}
          </button>
        </div>
      </div>
    </div>
  );
}
